import { useId } from 'react';
import { SvgIcon } from 'components/helpers/SvgIcon';
import { useContentQuery } from './ContentQueryContext';
import styles from './Pagination.module.scss';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import useDictionary from 'lib/hooks/useDictionary';
import { useIsMobile } from 'lib/hooks/useIsMobile';
import {
  pushSearchPageChangeEvent,
  SearchAnalyticsType,
} from 'components/helpers/Analytics/SearchAndFilters/SearchAndFiltersAnalytics';
import { DEFAULT_CONTENT_PAGE_SIZE } from '../CardListingShared';

export const Pagination = ({
  onPageChangeStart,
  searchType = 'Standard',
  searchAnalyticsEnabled = false,
}: {
  onPageChangeStart?: () => void;
  searchType?: SearchAnalyticsType;
  searchAnalyticsEnabled?: boolean;
}) => {
  const edgePageCount = 2;
  const paginationHeadingId = useId();
  const { query, searchResponse, setQuery } = useContentQuery();
  const isMobile = useIsMobile();

  const totalItems = searchResponse?.Count ?? 0;
  const itemsPerPage = searchResponse?.PageSize ?? DEFAULT_CONTENT_PAGE_SIZE;
  const currentPage = searchResponse?.PageIndex ?? 1;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const showPagination = totalItems > itemsPerPage;
  const { getDictionaryValue } = useDictionary();

  const prevPageLabel =
    getDictionaryValue('Components.AdvancedContentGrid.PreviousPage') || 'Previous page';
  const nextPageLabel =
    getDictionaryValue('Components.AdvancedContentGrid.NextPage') || 'Next page';
  const paginationLabel =
    getDictionaryValue('Components.AdvancedContentGrid.Pagination') || 'Pagination';

  if (!showPagination) {
    return null;
  }
  const handlePageChange = (page: number) => {
    // Guard against out-of-range values
    const newPage = Math.max(1, Math.min(page, totalPages || 1));
    if (newPage === (query?.pageIndex ?? currentPage)) return;
    if (searchAnalyticsEnabled) {
      pushSearchPageChangeEvent({
        searchType,
        siteSearchPhrase: query?.searchTerm ?? '',
        pagination: newPage.toString(),
      });
    }
    onPageChangeStart?.();
    setQuery?.({
      ...query,
      pageIndex: newPage,
    });
  };

  const renderPageButton = (page: number) => (
    <CtaButton
      key={page}
      onClick={() => handlePageChange(page)}
      text={page.toString()}
      ctaStyle="outline"
      ctaComponentClass="component-feature-full-color-cta-1-color"
      aria-current={page === currentPage ? 'page' : undefined}
      className={page === currentPage ? styles.active : undefined}
    />
  );

  const renderMobilePageNumbers = () => {
    if (totalPages <= 3) {
      return Array.from({ length: totalPages }, (_, index) => renderPageButton(index + 1));
    }

    const pages: (number | string)[] = [];

    if (currentPage <= 3) {
      pages.push(1, 2, 3, 'ellipsis-end', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, 'ellipsis-start', totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, 'ellipsis-start', currentPage, 'ellipsis-end', totalPages);
    }

    return pages.map((page, index) => {
      if (page === 'ellipsis-start' || page === 'ellipsis-end') {
        return (
          <span key={`mobile-ellipsis-${index}`} className={styles.paginationEllipsis}>
            ...
          </span>
        );
      }

      return renderPageButton(page as number);
    });
  };

  const renderPageNumbers = () => {
    if (isMobile) {
      return renderMobilePageNumbers();
    }

    if (totalPages <= 5) {
      // Show all pages if 5 or fewer
      return Array.from({ length: totalPages }, (_, index) => renderPageButton(index + 1));
    }

    const pages: (number | string)[] = [];

    if (currentPage <= edgePageCount + 1) {
      // Near the start: show 1, 2, 3, ..., last
      for (let i = 1; i <= edgePageCount + 1; i++) {
        pages.push(i);
      }
      pages.push('ellipsis-end');
      pages.push(totalPages);
    } else if (currentPage >= totalPages - edgePageCount) {
      // Near the end: show 1, ..., last-2, last-1, last
      pages.push(1);
      pages.push('ellipsis-start');
      for (let i = totalPages - edgePageCount; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // In the middle: show 1, ..., current-1, current, current+1, ..., last
      pages.push(1);
      pages.push('ellipsis-start');
      pages.push(currentPage - 1);
      pages.push(currentPage);
      pages.push(currentPage + 1);
      pages.push('ellipsis-end');
      pages.push(totalPages);
    }

    return pages.map((page, index) => {
      if (page === 'ellipsis-start' || page === 'ellipsis-end') {
        return (
          <span key={`ellipsis-${index}`} className={styles.paginationEllipsis}>
            ...
          </span>
        );
      }
      return renderPageButton(page as number);
    });
  };

  return (
    <nav className={styles.paginationContainer} aria-labelledby={paginationHeadingId}>
      <h2 id={paginationHeadingId} className="screenReaderText">
        {paginationLabel}
      </h2>
      <ul className={styles.pagination}>
        <li>
          <CtaButton
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            ctaStyle="none"
            ctaComponentClass="component-feature-full-color-cta-1-color"
            aria-label={prevPageLabel}
          >
            <SvgIcon icon="chevron-left" size="sm" viewBox="0 0 24 24" />
          </CtaButton>
        </li>
        {renderPageNumbers().map((page) => (
          <li key={(page as React.ReactElement).key ?? undefined}>{page}</li>
        ))}
        <li>
          <CtaButton
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            ctaStyle="none"
            ctaComponentClass="component-feature-full-color-cta-1-color"
            aria-label={nextPageLabel}
          >
            <SvgIcon
              className={styles.nextPageIcon}
              icon="chevron-right"
              size="sm"
              viewBox="0 0 24 24"
            />
          </CtaButton>
        </li>
      </ul>
    </nav>
  );
};
